import uniqBy from "lodash.uniqby";

import { createReducer } from "../../../common/reduxUtil";

import {
  SET_TYPING_DATA,
  SET_USERNAME,
  SET_CONVERSATIONS,
  APPEND_CONVERSATIONS,
  SET_CONVERSATION_PAGINATION,
  SET_CURRENT_ROOM,
  SET_CURRENT_ROOM_MESSAGES,
  UPDATE_CURRENT_ROOM_MESSAGES,
  UPDATE_CURRENT_ROOM_HISTORY,
  SET_PARTICIPANTS_DETAILS,
  UPDATE_CONVERSATION,
  SET_SYSTEM_MESSAGE_SEEN_DATA,
  ALL_MESSAGES_ARE_LOADED,
  MESSAGES_LOADING,
  SET_CONVERSATION_TOPICS,
  UPDATE_CONVERSATION_TOPIC,
  UPDATE_CONVERSATION_TOPICS,
  SET_CURRENT_TOPIC,
  CONVERSATION_TOPICS_LOADING,
  UPDATE_CONVERSATION_LAST_ACTIVITY,
  UPDATE_CONVERSATIONS_LIST,
  ADD_TO_SEND_MESSAGE_QUEUE,
  REMOVE_FROM_SEND_MESSAGE_QUEUE,
  UPDATE_USER_PERMISSIONS,
  SET_PARTICIPANTS_COUNT,
  UPDATE_LAST_SEEN,
  TOGGLE_CONVERSATION_INFO_PANEL,
  REMOVE_CONVERSATION_FROM_LIST,
  USER_ONLINE,
  CONVERSATION_HISTORY_FAILED,
  CLEAR_CONVERSATION_HISTORY_ERROR,
  SET_URL_CONVERSATION_LOADING,
  SET_URL_CONVERSATION_ERROR,
  SET_MAIL_MESSAGES,
  UPDATE_MAIL_HISTORY,
  ALL_MAIL_MESSAGES_LOADED,
  MESSAGE_EMAIL_SUMMARY,
  SET_AUTOMATION_STATUS,
  SET_OPS_AGENT_PAUSED,
  SET_CONVERSATION_FILTER,
} from "./types";

const initialState = {
  userName: "",
  chatLogs: {},
  sendMessageQueue: {},
  selectedConversationId: "",
  isTypingData: {},
  isOnline: false,
  conversations: [],
  participants: {},
  messageSeenAcknowledgment: {},
  allMessagesAreLoaded: {},
  isMessageLoading: {},
  topics: {},
  selectedConversationTopic: {},
  isConversationTopicLoading: false,
  userPresence: {},
  userPermissions: {},
  showConversationInfoPanel: false,
  messageFetchErrors: {},
  urlConversationLoading: {
    isLoading: false,
    conversationId: null,
    error: null,
  },
  mailChatLogs: {},
  allMailMessagesLoaded: {},
  automationStatuses: {},
  // Who the chat agent replies as, per conversation: { name, gamePlayerId }.
  // Anika for most leads, the assigned ops for paid leads. Drives the composer's
  // "agent is active" banner (shown to that responder before they reply manually).
  automationResponders: {},
  // The ops agent, per conversation: { enabled, paused }. `enabled` when it —
  // not Anika — answers the lead (a paid lead in the rollout); `paused` is its
  // own switch, independent of Anika's in automationStatuses.
  opsAgentStatuses: {},
  conversationFilter: null,
  // Pagination state for the conversations list. `serverPage` is the last
  // server page fetched per type (initial load = 0). `hasMore` combines the
  // totalGroups/totalDMs ceiling with a "last page was full" safety net so
  // the Load More button stays consistent even when totals are stale.
  conversationPagination: {
    groupsServerPage: 0,
    dmsServerPage: 0,
    totalGroups: 0,
    totalDMs: 0,
    hasMoreGroups: false,
    hasMoreDMs: false,
    isLoadingMoreGroups: false,
    isLoadingMoreDMs: false,
  },
};

const initialConversationPagination = initialState.conversationPagination;

export default createReducer(initialState, {
  [SET_USERNAME]: (state, payload) => {
    return Object.assign({}, state, {
      userName: payload.userName,
    });
  },
  [SET_CONVERSATIONS]: (state, payload) => {
    // SET_CONVERSATIONS is a fresh load (initial mount, filter change,
    // forced refresh), so pagination resets back to initial. Subsequent
    // SET_CONVERSATION_PAGINATION calls from the operation seed the new
    // totals.
    const base = Object.assign({}, state, {
      conversationPagination: initialConversationPagination,
    });

    // If we have a selected conversation, preserve it in the new list
    if (state.selectedConversationId && state.conversations.length > 0) {
      const selectedConv = state.conversations.find(
        (conv) => conv.conversationId === state.selectedConversationId
      );

      if (selectedConv) {
        // Check if the selected conversation exists in the new payload
        const existsInNewList = payload.some(
          (conv) => conv.conversationId === state.selectedConversationId
        );

        if (!existsInNewList) {
          // Add the selected conversation to the beginning of the new list
          // to ensure it's not lost during the update
          return Object.assign({}, base, {
            conversations: [selectedConv, ...payload],
          });
        }
      }
    }

    return Object.assign({}, base, {
      conversations: payload,
    });
  },
  [APPEND_CONVERSATIONS]: (state, payload) => {
    if (!Array.isArray(payload) || payload.length === 0) return state;
    // De-dup by conversationId — fresh server copy wins so we pick up any
    // changes (lastActivity, isPinned, etc.) that may have shifted since
    // the conversation was first loaded.
    const incomingById = new Map(
      payload.map((conv) => [conv.conversationId, conv])
    );
    const merged = state.conversations.map((conv) =>
      incomingById.has(conv.conversationId)
        ? incomingById.get(conv.conversationId)
        : conv
    );
    const existingIds = new Set(
      state.conversations.map((conv) => conv.conversationId)
    );
    for (const conv of payload) {
      if (!existingIds.has(conv.conversationId)) {
        merged.push(conv);
      }
    }
    return Object.assign({}, state, { conversations: merged });
  },
  [SET_CONVERSATION_PAGINATION]: (state, payload) => {
    return Object.assign({}, state, {
      conversationPagination: {
        ...state.conversationPagination,
        ...payload,
      },
    });
  },
  [SET_TYPING_DATA]: (state, payload) => {
    if (state.userName === payload.userName) {
      return state;
    }
    return Object.assign({}, state, {
      isTypingData: payload,
    });
  },
  [SET_SYSTEM_MESSAGE_SEEN_DATA]: (state, payload) => {
    if (state.userName === payload.messageSeenBy) {
      return state;
    }

    const currentAck = state.messageSeenAcknowledgment?.[payload.roomName];

    // For groups: Track multiple users who have read (build a Set to avoid duplicates)
    let readByUsers = new Set();
    if (currentAck?.messageSeenByGroupUsers) {
      readByUsers = new Set(currentAck.messageSeenByGroupUsers);
    } else if (currentAck?.messageSeenBy) {
      // Migrate old single-user format to new array format
      readByUsers.add(currentAck.messageSeenBy);
    }

    // Add the new user who just read
    readByUsers.add(payload.messageSeenBy);

    return Object.assign({}, state, {
      messageSeenAcknowledgment: {
        ...state.messageSeenAcknowledgment,
        [payload.roomName]: {
          messageSeenAt: payload.messageSeenAt,
          messageSeenBy: payload.messageSeenBy, // Keep for DM backward compatibility
          messageSeenByGroupUsers: Array.from(readByUsers), // NEW: Array of all users who read
        },
      },
    });
  },
  [SET_CURRENT_ROOM]: (state, payload) => {
    return Object.assign({}, state, {
      selectedConversationId: payload.conversationId,
      allMessagesAreLoaded: {
        [payload.conversationId]: false,
      },
      allMailMessagesLoaded: {
        ...state.allMailMessagesLoaded,
        [payload.conversationId]: false,
      },
    });
  },
  [SET_CURRENT_ROOM_MESSAGES]: (
    state,
    { conversationId, messages, topicId }
  ) => {
    if (!messages) return state;
    return Object.assign({}, state, {
      chatLogs: {
        ...state.chatLogs,
        [conversationId]: topicId
          ? {
              ...state.chatLogs?.[conversationId],
              [topicId]: [...messages],
            }
          : [...messages],
      },
    });
  },
  [UPDATE_CURRENT_ROOM_MESSAGES]: (state, { conversationId, message }) => {
    // Check if message is an email (for mailChatLogs sync)
    const isEmailMessage =
      message?.metadata?.isEmail ||
      message?.mailId ||
      message?.attachment?.some?.((att) => att?.type === MESSAGE_EMAIL_SUMMARY);
    const hasMailLogs = Array.isArray(state.mailChatLogs[conversationId]);

    if (message.isEdited || message.isDeleted || message.isHuddleEnded) {
      const updateMessages = (messages) => {
        return messages.map((msg) => {
          if (msg.messageId === message.messageId) {
            delete message.timestamp;
            if (
              (message.isCallHighlightEdit || message.isCallFeedback) &&
              message.true_author
            ) {
              message.author = message.true_author;
            }
            return { ...msg, ...message };
          }
          return msg;
        });
      };

      const newState = {
        chatLogs: {
          ...state.chatLogs,
          [conversationId]: !message.topicId
            ? state.chatLogs[conversationId]
              ? updateMessages(state.chatLogs[conversationId])
              : [message]
            : {
                ...state.chatLogs[conversationId],
                [message.topicId]: state.chatLogs[conversationId]?.[
                  message.topicId
                ]
                  ? updateMessages(
                      state.chatLogs[conversationId][message.topicId]
                    )
                  : [message],
              },
        },
      };

      // Also update mailChatLogs if the message is an email edit/delete
      if (isEmailMessage && hasMailLogs) {
        newState.mailChatLogs = {
          ...state.mailChatLogs,
          [conversationId]: updateMessages(state.mailChatLogs[conversationId]),
        };
      }

      return Object.assign({}, state, newState);
    }

    const newState = {
      chatLogs: {
        ...state.chatLogs,
        [conversationId]: !message.topicId
          ? state.chatLogs[conversationId]
            ? uniqBy([...state.chatLogs[conversationId], message], "messageId")
            : [message]
          : {
              ...state.chatLogs[conversationId],
              [message.topicId]: state.chatLogs[conversationId]?.[
                message.topicId
              ]
                ? uniqBy(
                    [
                      ...state.chatLogs[conversationId][message.topicId],
                      message,
                    ],
                    "messageId"
                  )
                : [message],
            },
      },
    };

    // Also append to mailChatLogs if the message is an email
    if (isEmailMessage && hasMailLogs) {
      newState.mailChatLogs = {
        ...state.mailChatLogs,
        [conversationId]: uniqBy(
          [...state.mailChatLogs[conversationId], message],
          "messageId"
        ),
      };
    }

    return Object.assign({}, state, newState);
  },
  [UPDATE_CURRENT_ROOM_HISTORY]: (
    state,
    { conversationId, messages, topicId }
  ) => {
    let allMessages = null;
    if (!topicId) {
      if (state.chatLogs[conversationId]) {
        allMessages = uniqBy(
          [...state.chatLogs[conversationId], ...messages],
          "messageId"
        ).sort((a, b) => a["timestamp"] - b["timestamp"]);
      } else {
        allMessages = messages;
      }
    } else {
      allMessages = {
        ...state.chatLogs[conversationId],
      };
      if (state.chatLogs[conversationId]?.[topicId]) {
        allMessages[topicId] = uniqBy(
          [...messages, ...state.chatLogs[conversationId][topicId]],
          "messageId"
        ).sort((a, b) => a["timestamp"] - b["timestamp"]);
      } else {
        allMessages[topicId] = messages;
      }
    }
    return Object.assign({}, state, {
      chatLogs: {
        ...state.chatLogs,
        [conversationId]: allMessages,
      },
    });
  },
  [SET_PARTICIPANTS_DETAILS]: (state, { conversationId, participants }) => {
    return Object.assign({}, state, {
      participants: {
        ...state.participants,
        [conversationId]: state.participants[conversationId]
          ? {
              ...state.participants[conversationId],
              list: state.participants[conversationId].list?.length
                ? uniqBy(
                    [
                      ...state.participants[conversationId].list,
                      ...participants,
                    ],
                    "participantId"
                  )
                : participants,
            }
          : {
              list: participants,
              count: participants?.length,
            },
      },
    });
  },
  [SET_PARTICIPANTS_COUNT]: (state, { conversationId, count }) => {
    return Object.assign({}, state, {
      participants: {
        ...state.participants,
        [conversationId]: state.participants[conversationId]
          ? {
              ...state.participants[conversationId],
              count,
            }
          : {
              count,
            },
      },
    });
  },
  [ALL_MESSAGES_ARE_LOADED]: (state, { conversationId, topicId }) => {
    return Object.assign({}, state, {
      allMessagesAreLoaded: {
        [conversationId]: topicId
          ? {
              [topicId]: true,
            }
          : true,
      },
    });
  },
  [UPDATE_CONVERSATION]: (state, payload) => {
    const index = state.conversations.findIndex(
      (obj) => obj.conversationId === payload.conversationId
    );

    if (index === -1) {
      return state;
    }

    // Create a copy of all conversations
    const updatedConversationsArray = [...state.conversations];

    // Update the conversation
    updatedConversationsArray[index] = {
      ...updatedConversationsArray[index],
      ...payload.conversation,
      conversation: {
        ...updatedConversationsArray[index].conversation,
        ...payload.conversation.conversation,
      },
    };

    // Sort conversations, preserving the selected conversation state
    const sortedConversations = updatedConversationsArray.sort((a, b) => {
      // First, sort by the pinned status: pinned messages (isPinned === true) come first.
      if (b.isPinned !== a.isPinned) {
        return b.isPinned - a.isPinned;
      }

      // If both are pinned or both are unpinned, sort by unread messages: unread messages (hasUnseenMessages === 1) come first.
      return b.hasUnseenMessages - a.hasUnseenMessages;
    });

    return Object.assign({}, state, {
      conversations: sortedConversations,
    });
  },
  [MESSAGES_LOADING]: (state, payload) => {
    return Object.assign({}, state, {
      isMessageLoading: {
        ...state.isMessageLoading,
        ...payload,
      },
    });
  },
  [SET_CONVERSATION_TOPICS]: (state, payload) => {
    return Object.assign({}, state, {
      topics: {
        ...payload,
      },
    });
  },
  [UPDATE_CONVERSATION_TOPIC]: (state, { conversationId, topic }) => {
    return Object.assign({}, state, {
      topics: {
        ...state.topics,
        [conversationId]: state.topics[conversationId]
          ? [topic, ...state.topics[conversationId]]
          : [topic],
      },
    });
  },
  [UPDATE_CONVERSATION_TOPICS]: (state, { conversationId, topics, total }) => {
    return Object.assign({}, state, {
      topics: {
        ...state.topics,
        [conversationId]: state.topics[conversationId]
          ? [...state.topics[conversationId], ...topics]
          : topics,
        total,
      },
    });
  },
  [SET_CURRENT_TOPIC]: (state, payload) =>
    Object.assign({}, state, {
      selectedConversationTopic: payload,
    }),
  [CONVERSATION_TOPICS_LOADING]: (state, payload) =>
    Object.assign({}, state, {
      isConversationTopicLoading: payload,
    }),
  [UPDATE_CONVERSATION_LAST_ACTIVITY]: (
    state,
    { conversationId, message, topic }
  ) => {
    const index = state.conversations.findIndex(
      (obj) => obj.conversationId === conversationId
    );

    if (index === -1) {
      return state;
    }

    // Create a copy of all conversations with the updated one
    const updatedConversationsArray = [...state.conversations];
    const existing = updatedConversationsArray[index];

    // Keep the unread badge in step with the message that just arrived,
    // mirroring ConversationService.updateParticipantsLastActivity() exactly —
    // this is the optimistic half of the same rule, applied until the next
    // conversation.list refetch replaces it with the server's value.
    //
    // Being system-generated only matters for the account the message is
    // ATTRIBUTED to — exactly as on the server. Widgets (call summaries,
    // delivery comments, SOP/LOR mirrors) post as a real employee, and
    // treating that as "they just spoke, so they must have read the room" is
    // what used to wipe their unread dot in the open tab with no refresh in
    // sight. For a message from anyone ELSE, system-generated or not, the
    // badge goes up: most inbound traffic is server-generated too — a
    // student's WhatsApp reply, an inbound email card, an Anika reply — and
    // suppressing those would lose the live dot on real incoming messages.
    const isViewing = conversationId === state.selectedConversationId;
    const isSystemGenerated = message?.metadata?.isSystemGenerated === true;
    const isMine = message?.author === state.userName;

    let hasUnseenMessages;
    if (isViewing) {
      hasUnseenMessages = 0;
    } else if (!isMine) {
      hasUnseenMessages = 1;
    } else if (isSystemGenerated) {
      hasUnseenMessages = existing?.hasUnseenMessages ?? 0;
    } else {
      hasUnseenMessages = 0;
    }

    // Update the conversation with new lastActivity
    updatedConversationsArray[index] = {
      ...existing,
      hasUnseenMessages,
      // Kept in step because utils.hasUnseenMessages prefers this field.
      hasUnread: hasUnseenMessages === 1,
      conversation: {
        ...existing?.conversation,
        lastActivity: message,
      },
    };

    // Sort conversations - but first check if the currently selected conversation exists
    const selectedConversation = state.selectedConversationId
      ? updatedConversationsArray.find(
          (conv) => conv.conversationId === state.selectedConversationId
        )
      : null;

    // Sort conversations by pinned status and unread messages
    const sortedConversations = updatedConversationsArray.sort((a, b) => {
      // First, sort by the pinned status: pinned messages (isPinned === true) come first.
      if (b.isPinned !== a.isPinned) {
        return b.isPinned - a.isPinned;
      }

      // If both are pinned or both are unpinned, sort by unread messages: unread messages (hasUnseenMessages === 1) come first.
      return b.hasUnseenMessages - a.hasUnseenMessages;
    });

    return Object.assign({}, state, {
      conversations: sortedConversations,
    });
  },
  [UPDATE_CONVERSATIONS_LIST]: (state, payload) => {
    const index = state.conversations.findIndex(
      (obj) => obj.conversationId === payload.conversationId
    );
    if (index === -1) {
      const updatedConversation = [payload, ...state.conversations].sort(
        (a, b) => {
          // First, sort by the pinned status: pinned messages (isPinned === true) come first.
          if (b.isPinned !== a.isPinned) {
            return b.isPinned - a.isPinned;
          }

          // If both are pinned or both are unpinned, sort by unread messages: unread messages (hasUnseenMessages === 1) come first.
          return b.hasUnseenMessages - a.hasUnseenMessages;
        }
      );
      return Object.assign({}, state, {
        conversations: updatedConversation,
      });
    }
    return state;
  },
  [REMOVE_CONVERSATION_FROM_LIST]: (state, payload) => {
    const conversations = state.conversations.filter(
      (conversation) => conversation.conversationId !== payload.conversationId
    );
    return Object.assign({}, state, {
      conversations,
      selectedConversationId: null,
    });
  },
  [ADD_TO_SEND_MESSAGE_QUEUE]: (state, payload) => {
    const existingQueue = state.sendMessageQueue[payload.conversationId] || [];

    // If the message has a temporaryId, check if it already exists in the queue
    if (payload.message.temporaryId) {
      const isDuplicate = existingQueue.some(
        (msg) => msg.temporaryId === payload.message.temporaryId
      );

      // If it's a duplicate, don't add it to the queue
      if (isDuplicate) {
        return state;
      }
    }

    return Object.assign({}, state, {
      sendMessageQueue: {
        ...state.sendMessageQueue,
        [payload.conversationId]: [...existingQueue, payload.message],
      },
    });
  },
  [REMOVE_FROM_SEND_MESSAGE_QUEUE]: (state, payload) => {
    const temp = [...state.sendMessageQueue[payload.conversationId]];
    temp.splice(payload.index, 1);
    return Object.assign({}, state, {
      sendMessageQueue: {
        ...state.sendMessageQueue,
        [payload.conversationId]: temp,
      },
    });
  },
  [UPDATE_USER_PERMISSIONS]: (state, payload) => {
    const { eventId, update } = payload;
    return Object.assign({}, state, {
      userPermissions: {
        ...state.userPermissions,
        [eventId]: {
          ...state.userPermissions[eventId],
          ...update,
        },
      },
    });
  },
  [UPDATE_LAST_SEEN]: (state, payload) => {
    const { conversationId, lastSeen } = payload;

    // Create a copy of conversations and update the lastSeen value
    const updatedConversations = state.conversations.map((conversation) => {
      if (conversation.conversationId === conversationId) {
        return {
          ...conversation,
          lastSeen,
        };
      }
      return conversation;
    });

    // Sort conversations, properly maintaining the selected conversation
    const sortedConversations = updatedConversations.sort((a, b) => {
      // First, sort by the pinned status: pinned messages (isPinned === true) come first.
      if (b.isPinned !== a.isPinned) {
        return b.isPinned - a.isPinned;
      }

      // If both are pinned or both are unpinned, sort by unread messages: unread messages (hasUnseenMessages === 1) come first.
      return b.hasUnseenMessages - a.hasUnseenMessages;
    });

    return Object.assign({}, state, {
      conversations: sortedConversations,
    });
  },
  [TOGGLE_CONVERSATION_INFO_PANEL]: (state, payload) => {
    return Object.assign({}, state, {
      showConversationInfoPanel: payload.shouldShow,
    });
  },
  [USER_ONLINE]: (state, payload) => {
    return Object.assign({}, state, {
      isOnline: payload.isOnline,
    });
  },
  [CONVERSATION_HISTORY_FAILED]: (state, payload) => {
    const { conversationId, topicId, error } = payload;
    return Object.assign({}, state, {
      messageFetchErrors: {
        ...state.messageFetchErrors,
        [conversationId]: {
          ...(state.messageFetchErrors[conversationId] || {}),
          [topicId || "_default"]: error,
        },
      },
    });
  },
  [CLEAR_CONVERSATION_HISTORY_ERROR]: (state, payload) => {
    const { conversationId, topicId } = payload;

    // If there are no errors for this conversation, return state as is
    if (!state.messageFetchErrors[conversationId]) {
      return state;
    }

    // Create a copy of the errors for this conversation
    const conversationErrors = { ...state.messageFetchErrors[conversationId] };

    // Delete the specific error entry
    delete conversationErrors[topicId || "_default"];

    // Create updated messageFetchErrors
    const updatedMessageFetchErrors = {
      ...state.messageFetchErrors,
      [conversationId]: conversationErrors,
    };

    // If there are no more errors for this conversation, remove the conversation entry
    if (Object.keys(conversationErrors).length === 0) {
      delete updatedMessageFetchErrors[conversationId];
    }

    return Object.assign({}, state, {
      messageFetchErrors: updatedMessageFetchErrors,
    });
  },
  [SET_URL_CONVERSATION_LOADING]: (state, payload) => {
    return Object.assign({}, state, {
      urlConversationLoading: {
        isLoading: payload.isLoading,
        conversationId: payload.conversationId,
        error: null,
      },
    });
  },
  [SET_URL_CONVERSATION_ERROR]: (state, payload) => {
    return Object.assign({}, state, {
      urlConversationLoading: {
        ...state.urlConversationLoading,
        isLoading: false,
        error: payload,
      },
    });
  },
  [SET_MAIL_MESSAGES]: (state, { conversationId, messages }) => {
    if (!messages) return state;
    return Object.assign({}, state, {
      mailChatLogs: {
        ...state.mailChatLogs,
        [conversationId]: [...messages],
      },
    });
  },
  [UPDATE_MAIL_HISTORY]: (state, { conversationId, messages }) => {
    const existing = state.mailChatLogs[conversationId];
    const allMessages = existing
      ? uniqBy([...existing, ...messages], "messageId").sort(
          (a, b) => a["timestamp"] - b["timestamp"]
        )
      : messages;
    return Object.assign({}, state, {
      mailChatLogs: {
        ...state.mailChatLogs,
        [conversationId]: allMessages,
      },
    });
  },
  [ALL_MAIL_MESSAGES_LOADED]: (state, { conversationId }) => {
    return Object.assign({}, state, {
      allMailMessagesLoaded: {
        ...state.allMailMessagesLoaded,
        [conversationId]: true,
      },
    });
  },
  [SET_AUTOMATION_STATUS]: (state, payload) => {
    return Object.assign({}, state, {
      automationStatuses: {
        ...state.automationStatuses,
        [payload.conversationId]: payload.isPaused,
      },
      // Keep any known responder when the toggle dispatches without one.
      automationResponders: {
        ...state.automationResponders,
        [payload.conversationId]:
          payload.respondsAs ||
          state.automationResponders[payload.conversationId] ||
          null,
      },
      // Likewise the ops agent's state: Anika's toggle dispatches without it.
      opsAgentStatuses: {
        ...state.opsAgentStatuses,
        [payload.conversationId]:
          payload.opsAgent ||
          state.opsAgentStatuses[payload.conversationId] ||
          null,
      },
    });
  },
  [SET_OPS_AGENT_PAUSED]: (state, payload) => {
    return Object.assign({}, state, {
      opsAgentStatuses: {
        ...state.opsAgentStatuses,
        [payload.conversationId]: {
          ...state.opsAgentStatuses[payload.conversationId],
          paused: payload.paused,
        },
      },
    });
  },
  [SET_CONVERSATION_FILTER]: (state, payload) => {
    return Object.assign({}, state, {
      conversationFilter: payload,
    });
  },
});
